import { useState, useCallback, useEffect, useRef } from 'react';
import { cn, formatDateTime } from '../../lib/utils';
import { Button } from '../ui/Button';
import { ConfirmModal, Modal } from '../ui/Modal';
import { SkeletonNotes } from '../ui/Skeleton';
import type { AnalystNote, NoteType, NewNote, EnrichedIncident } from '../../data/types';
import { 
  getNotes, 
  addNote, 
  deleteNote,
  makeNoteCustomerVisible, 
  syncNoteToDefender,
  getNoteTypeLabel,
  getNoteTypeColor,
} from '../../services/notes';
import { useUser } from '../../context';
import { useIsMobile } from '../../hooks/useMediaQuery';

interface AnalystNotesProps {
  tenantId: string;
  incidentId: string;
  className?: string;
  /** Incident context for the side pane */
  incidentContext?: EnrichedIncident | null;
}

/**
 * Chat-style notes system with scrollable list and anchored input.
 * Includes a context pane showing key incident information.
 */
export function AnalystNotes({ tenantId, incidentId, className, incidentContext }: AnalystNotesProps) {
  const [notes, setNotes] = useState<AnalystNote[]>([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Fetch notes
  const fetchNotes = useCallback(async () => {
    setLoading(true);
    const response = await getNotes(tenantId, incidentId);
    if (response.success) {
      setNotes(response.data);
    }
    setLoading(false);
  }, [tenantId, incidentId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // Auto-scroll to bottom when notes change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [notes]);

  // Sort notes chronologically (oldest first, newest at bottom)
  const sortedNotes = [...notes].sort((a, b) => 
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  // Group notes by type for visual distinction
  const isCustomerNote = (note: AnalystNote) => !note.isInternal;

  if (loading) {
    return <SkeletonNotes count={3} className={className} />;
  }

  return (
    <div className={cn('flex gap-4 h-[calc(100dvh-220px)] min-h-[400px] md:h-[calc(100vh-300px)] md:min-h-[500px]', className)}>
      {/* Main Chat Area */}
      <div className="flex-1 min-w-0 flex flex-col bg-bg-secondary rounded-xl border border-border-default overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-border-default bg-bg-tertiary flex-shrink-0">
          <h3 className="text-sm font-medium text-text-primary flex items-center gap-2">
            <ChatIcon className="w-4 h-4 text-accent-blue" />
            Incident Notes
            <span className="text-text-secondary">({notes.length})</span>
          </h3>
        </div>

        {/* Scrollable Chat Messages */}
        <div 
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-4 space-y-4"
        >
          {sortedNotes.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-text-secondary">
                <ChatIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No notes yet. Start documenting below.</p>
              </div>
            </div>
          ) : (
            sortedNotes.map((note) => (
              <ChatMessage
                key={note.noteId}
                note={note}
                isCustomer={isCustomerNote(note)}
                onRefresh={fetchNotes}
              />
            ))
          )}
        </div>

        {/* Input Area - Anchored to bottom */}
        <div className="border-t border-border-default flex-shrink-0">
          <AddNoteForm
            tenantId={tenantId}
            incidentId={incidentId}
            onSuccess={fetchNotes}
          />
        </div>
      </div>

      {/* Context Pane (desktop only — too wide below lg) */}
      {incidentContext && (
        <div className="hidden lg:block w-[22rem] flex-shrink-0">
          <ContextPane incident={incidentContext} />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// CHAT MESSAGE (bubble style)
// ============================================================================

interface ChatMessageProps {
  note: AnalystNote;
  isCustomer: boolean;
  onRefresh: () => void;
}

function ChatMessage({ note, isCustomer, onRefresh }: ChatMessageProps) {
  const [showActions, setShowActions] = useState(false);
  // Touch devices have no hover — keep actions visible on mobile
  const isMobile = useIsMobile();
  const [showMakeVisibleConfirm, setShowMakeVisibleConfirm] = useState(false);
  const [showPushConfirm, setShowPushConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleMakeVisible = async () => {
    setLoading(true);
    await makeNoteCustomerVisible(note.tenantId, note.incidentId, note.noteId);
    setShowMakeVisibleConfirm(false);
    setLoading(false);
    onRefresh();
  };

  const handlePushToDefender = async () => {
    setLoading(true);
    await syncNoteToDefender(note.tenantId, note.incidentId, note.content);
    setShowPushConfirm(false);
    setLoading(false);
    onRefresh();
  };

  const handleDelete = async () => {
    setLoading(true);
    await deleteNote(note.tenantId, note.incidentId, note.noteId);
    setShowDeleteConfirm(false);
    setLoading(false);
    onRefresh();
  };

  // Determine visibility indicator
  const getVisibilityIndicator = () => {
    if (note.syncedToDefender) {
      return { icon: <CheckCircleIcon className="w-3 h-3" />, label: 'In Defender', color: 'text-green-500' };
    }
    if (!note.isInternal) {
      return { icon: <EyeIcon className="w-3 h-3" />, label: 'Customer Visible', color: 'text-accent-blue' };
    }
    return { icon: <LockIcon className="w-3 h-3" />, label: 'Internal', color: 'text-text-muted' };
  };

  const visibility = getVisibilityIndicator();

  return (
    <>
      <div 
        className={cn(
          'group max-w-[85%]',
          isCustomer ? 'ml-auto' : 'mr-auto'
        )}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        {/* Message Bubble */}
        <div className={cn(
          'rounded-2xl px-4 py-3 relative',
          isCustomer 
            ? 'bg-brand-blue text-white rounded-br-md' 
            : 'bg-bg-tertiary text-text-primary rounded-bl-md'
        )}>
          {/* Author and metadata */}
          <div className={cn(
            'flex items-center gap-2 mb-1 text-xs',
            isCustomer ? 'text-blue-100' : 'text-text-secondary'
          )}>
            <span className="font-medium">{note.authorName}</span>
            <span>·</span>
            <span>{formatDate(note.createdAt)}</span>
            <span className={cn(
              'px-1.5 py-0.5 rounded text-xs font-medium',
              isCustomer ? 'bg-blue-400/30 text-white' : getNoteTypeColor(note.noteType) + ' text-white'
            )}>
              {getNoteTypeLabel(note.noteType)}
            </span>
          </div>

          {/* Content */}
          <p className="text-sm whitespace-pre-wrap">{note.content}</p>

          {/* Visibility badge */}
          <div className={cn(
            'flex items-center gap-1 mt-2 text-xs',
            isCustomer ? 'text-blue-200' : visibility.color
          )}>
            {visibility.icon}
            <span>{visibility.label}</span>
            {note.isPinned && <PinIcon className="w-3 h-3 text-amber-400 ml-1" />}
          </div>
        </div>

        {/* Actions - shown on hover (always visible on mobile) */}
        {(showActions || isMobile) && (
          <div className={cn(
            'flex items-center gap-2 mt-1',
            isCustomer ? 'justify-end' : 'justify-start'
          )}>
            {note.isInternal && (
              <button
                onClick={() => setShowMakeVisibleConfirm(true)}
                className="text-xs text-accent-blue hover:opacity-80 flex items-center gap-1"
              >
                <EyeIcon className="w-3 h-3" />
                Share
              </button>
            )}
            {!note.isInternal && !note.syncedToDefender && (
              <button
                onClick={() => setShowPushConfirm(true)}
                className="text-xs text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 flex items-center gap-1"
              >
                <UploadIcon className="w-3 h-3" />
                Push to Defender
              </button>
            )}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1"
            >
              <TrashIcon className="w-3 h-3" />
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Confirmation modals */}
      <ConfirmModal
        isOpen={showMakeVisibleConfirm}
        onClose={() => setShowMakeVisibleConfirm(false)}
        onConfirm={handleMakeVisible}
        title="Share Note with Customer"
        message="This note will become visible to the customer in their portal. This action cannot be undone."
        confirmLabel="Share Note"
        loading={loading}
      />

      <ConfirmModal
        isOpen={showPushConfirm}
        onClose={() => setShowPushConfirm(false)}
        onConfirm={handlePushToDefender}
        title="Push to Defender"
        message="This note will be added as a comment to the customer's Defender environment. This action cannot be undone."
        confirmLabel="Push to Defender"
        danger
        loading={loading}
      />

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        confirmLabel="Delete"
        danger
        loading={loading}
      />
    </>
  );
}

// ============================================================================
// ADD NOTE FORM
// ============================================================================

/** Inline composer grows with the note until this height, then scrolls. */
const INLINE_MAX_HEIGHT_PX = 220;

interface AddNoteFormProps {
  tenantId: string;
  incidentId: string;
  onSuccess: () => void;
}

function AddNoteForm({ tenantId, incidentId, onSuccess }: AddNoteFormProps) {
  const { currentUser } = useUser();
  const [noteType, setNoteType] = useState<NoteType>('comment');
  const [content, setContent] = useState('');
  const [isInternal, setIsInternal] = useState(true);
  const [syncToDefender, setSyncToDefender] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // AB#136: the inline box grows with the note (to a cap), and a pop-out
  // editor gives long notes a full-height field.
  const [expanded, setExpanded] = useState(false);
  const inlineRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = inlineRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, INLINE_MAX_HEIGHT_PX)}px`;
  }, [content]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    setSubmitting(true);
    const newNote: NewNote = {
      noteType,
      content: content.trim(),
      isInternal,
      syncToDefender: !isInternal && syncToDefender,
    };

    await addNote(tenantId, incidentId, newNote);
    setSubmitting(false);
    setContent('');
    setExpanded(false);
    onSuccess();
  };

  // In the pop-out, Enter is a newline; Ctrl/Cmd+Enter sends.
  const handleExpandedKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (content.trim()) {
        handleSubmit(e as unknown as React.FormEvent);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (content.trim()) {
        handleSubmit(e as unknown as React.FormEvent);
      }
    }
  };

  const displayName = currentUser?.displayName || 'Unknown User';

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-3">
      {/* Input row */}
      <div className="flex items-end gap-3">
        {/* Avatar */}
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
          {displayName.charAt(0)}
        </div>

        {/* Textarea (auto-grows to INLINE_MAX_HEIGHT_PX, then scrolls) */}
        <div className="flex-1 relative">
          <textarea
            ref={inlineRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your note... (Enter to send, Shift+Enter for new line)"
            rows={2}
            className="w-full px-4 py-2.5 pr-10 bg-bg-tertiary border border-border-default rounded-2xl text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue resize-none overflow-y-auto"
          />
          <button
            type="button"
            onClick={() => setExpanded(true)}
            title="Open larger editor"
            aria-label="Open larger editor"
            className="absolute right-2 top-2 p-1.5 rounded-full text-text-muted hover:text-text-primary hover:bg-bg-secondary transition-colors"
          >
            <ExpandIcon className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Pop-out editor */}
        <Modal
          isOpen={expanded}
          onClose={() => setExpanded(false)}
          title="Write note"
          description="Enter for a new line. Ctrl+Enter (⌘+Enter on Mac) to add the note."
          size="2xl"
          footer={
            <div className="flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setExpanded(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!content.trim() || submitting}
                onClick={(e) => handleSubmit(e as unknown as React.FormEvent)}
              >
                <SendIcon className="w-4 h-4 mr-1.5" />
                Add note
              </Button>
            </div>
          }
        >
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleExpandedKeyDown}
            autoFocus
            rows={16}
            placeholder="Type your note..."
            className="w-full px-4 py-3 bg-bg-tertiary border border-border-default rounded-xl text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue resize-y min-h-[16rem]"
          />
        </Modal>

        {/* Send button */}
        <Button 
          type="submit" 
          size="sm" 
          disabled={!content.trim() || submitting}
          className="rounded-full w-10 h-10 p-0 flex-shrink-0"
        >
          <SendIcon className="w-4 h-4" />
        </Button>
      </div>

      {/* Options row */}
      <div className="flex items-center gap-2 pl-11">
        {/* Note type selector */}
        <select
          value={noteType}
          onChange={(e) => setNoteType(e.target.value as NoteType)}
          className="text-xs px-2 py-1.5 rounded-lg border border-border-default bg-bg-secondary text-text-secondary focus:outline-none focus:ring-2 focus:ring-brand-blue"
        >
          <option value="comment">Comment</option>
          <option value="investigation_note">Investigation</option>
          <option value="recommendation">Recommendation</option>
          <option value="escalation">Escalation</option>
        </select>

        {/* Visibility toggle */}
        <button
          type="button"
          onClick={() => {
            setIsInternal(!isInternal);
            if (isInternal) setSyncToDefender(true);
            else setSyncToDefender(false);
          }}
          className={cn(
            'flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors',
            isInternal
              ? 'bg-bg-tertiary text-text-secondary'
              : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
          )}
        >
          {isInternal ? (
            <>
              <LockIcon className="w-3 h-3" />
              Internal
            </>
          ) : (
            <>
              <EyeIcon className="w-3 h-3" />
              Customer
            </>
          )}
        </button>

        {/* Sync to Defender option */}
        {!isInternal && (
          <button
            type="button"
            onClick={() => setSyncToDefender(!syncToDefender)}
            className={cn(
              'flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors',
              syncToDefender
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                : 'bg-bg-tertiary text-text-secondary'
            )}
          >
            <UploadIcon className="w-3 h-3" />
            Defender
          </button>
        )}
      </div>
    </form>
  );
}

// ============================================================================
// CONTEXT PANE
// ============================================================================

interface ContextPaneProps {
  incident: EnrichedIncident;
}

function ContextPane({ incident }: ContextPaneProps) {
  const ai = incident.aiEnrichment;
  
  return (
    <div className="bg-bg-secondary rounded-xl border border-border-default overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border-default bg-bg-tertiary flex-shrink-0">
        <h3 className="text-sm font-medium text-text-primary flex items-center gap-2">
          <InfoIcon className="w-4 h-4 text-accent-blue" />
          Quick Reference
        </h3>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Incident ID */}
        <ContextItem 
          label="Incident" 
          value={`#${incident.incidentId}`}
          highlight
        />

        {/* Severity */}
        <ContextItem 
          label="Severity" 
          value={incident.severity}
          badge={incident.severity}
        />

        {/* Status */}
        <ContextItem 
          label="Status" 
          value={incident.platformStatus}
        />

        {/* Risk Score */}
        {(incident.riskScore ?? ai?.riskScore) && (
          <ContextItem 
            label="Risk Score" 
            value={`${incident.riskScore ?? ai?.riskScore}/100`}
            highlight
          />
        )}

        {/* Classification */}
        {incident.aiClassification && (
          <ContextItem 
            label="Classification" 
            value={incident.aiClassification}
          />
        )}

        {/* Title */}
        <div className="pt-2 border-t border-border-light">
          <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Title</div>
          <p className="text-sm text-text-primary line-clamp-3">
            {incident.title}
          </p>
        </div>

        {/* Summary */}
        {(incident.summary || ai?.summary) && (
          <div className="pt-2 border-t border-border-light">
            <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Summary</div>
            <p className="text-xs text-text-secondary line-clamp-4">
              {incident.summary || ai?.summary}
            </p>
          </div>
        )}

        {/* Immediate Action */}
        {ai?.immediateAction && (
          <div className="pt-2 border-t border-border-light">
            <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Recommended Action</div>
            <p className="text-xs text-text-secondary">
              {ai.immediateAction}
            </p>
          </div>
        )}

        {/* Affected Asset */}
        {ai?.affectedAsset && (
          <ContextItem 
            label="Affected Asset" 
            value={ai.affectedAsset}
          />
        )}

        {/* AI Tags */}
        {ai?.tags && ai.tags.length > 0 && (
          <div className="pt-2 border-t border-border-light">
            <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">Tags</div>
            <div className="flex flex-wrap gap-1">
              {ai.tags.slice(0, 5).map((tag: string, i: number) => (
                <span 
                  key={i} 
                  className="px-2 py-0.5 bg-bg-tertiary text-text-secondary rounded text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Created time */}
        <ContextItem 
          label="Created" 
          value={formatDateTime(incident.createdTime)}
        />
      </div>
    </div>
  );
}

function ContextItem({ 
  label, 
  value, 
  highlight = false,
  badge
}: { 
  label: string; 
  value: string; 
  highlight?: boolean;
  badge?: string;
}) {
  const getBadgeColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'high': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'low': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      default: return 'bg-bg-tertiary text-text-secondary';
    }
  };

  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-text-secondary">{label}</span>
      {badge ? (
        <span className={cn('px-2 py-0.5 rounded text-xs font-medium capitalize', getBadgeColor(badge))}>
          {value}
        </span>
      ) : (
        <span className={cn(
          'text-sm',
          highlight ? 'font-semibold text-text-primary' : 'text-text-secondary'
        )}>
          {value}
        </span>
      )}
    </div>
  );
}

// ============================================================================
// ICONS
// ============================================================================

function ChatIcon({ className }: { className?: string }) {
  return (
    <svg className={cn('w-4 h-4', className)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={cn('w-4 h-4', className)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function ExpandIcon({ className }: { className?: string }) {
  return (
    <svg className={cn('w-4 h-4', className)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
    </svg>
  );
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={cn('w-4 h-4', className)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={cn('w-4 h-4', className)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={cn('w-4 h-4', className)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={cn('w-4 h-4', className)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={cn('w-4 h-4', className)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={cn('w-4 h-4', className)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
  );
}

function PinIcon({ className }: { className?: string }) {
  return (
    <svg className={cn('w-4 h-4', className)} fill="currentColor" viewBox="0 0 24 24">
      <path d="M16 4h-3.586L10 1.586 8.586 3H5a1 1 0 00-1 1v1a1 1 0 001 1l1 9v3a1 1 0 001 1h3v5l1 1 1-1v-5h3a1 1 0 001-1v-3l1-9a1 1 0 001-1V4a1 1 0 00-1-1z" />
    </svg>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}
